package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CardGiftcard
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Payments
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import org.gymly.data.AdminRepo
import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminMembershipOrderStatus
import org.gymly.model.AdminPaymentDiscount
import org.gymly.model.AdminPaymentMethod
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney

data class BulkPaymentFormResult(
    val status: AdminMembershipOrderStatus,
    val lines: List<PaymentLineDraft>,
    val changeGivenCents: Long = 0L,
    val changeGivenUsdCents: Long = 0L,
    val walletAppliedCents: Long = 0L,
    /** Pendiente explícito en USD; 0 = vacío. */
    val pendingUsdCents: Long = 0L,
    /** Pendiente explícito en Bs; 0 = vacío. Ambos vacíos = pago completo. */
    val pendingBsCents: Long = 0L,
    /** Descuento en Bs (solo orden de membresía); null = sin descuento. */
    val discount: AdminPaymentDiscount? = null,
) {
    val method: AdminPaymentMethod? get() = lines.singleOrNull()?.method
    /** Solo Bs (efectivo, móvil…). No mezcla montos en $. */
    val amountCents: Long get() = lines.sumOf { line ->
        if (line.method.usesUsdAmount()) 0L else parseBulkAmountToCents(line.amountInput)
    }
    val usdCents: Long get() = lines.sumOf { line ->
        if (!line.method.usesUsdAmount()) 0L else parseBulkAmountToCents(line.amountInput)
    }
    val payerPhone: String? get() = lines.singleOrNull()?.payerPhone?.ifBlank { null }
    val proof: PickedImagePayload? get() = lines.singleOrNull()?.proof
}

/**
 * Valida el descuento en Bs de un cobro de orden de membresía.
 * Devuelve el descuento (o null si no hay) y el mensaje de error a mostrar (o null si es válido).
 */
fun bulkDiscountOrError(
    bsInput: String,
    reason: String,
    note: String,
    hasPending: Boolean,
    hasWalletCredit: Boolean,
    hasLines: Boolean,
    usdInput: String = "",
): Pair<AdminPaymentDiscount?, String?> {
    val cents = parseBulkAmountToCents(bsInput)
    val usdCents = parseBulkAmountToCents(usdInput)
    if (cents <= 0L && usdCents <= 0L) return null to null
    val error = when {
        hasPending -> "El descuento solo se registra con el pendiente en 0,00."
        hasWalletCredit -> "El descuento no se combina con saldo a favor."
        !hasLines -> "El descuento requiere un cobro registrado."
        reason.isBlank() -> "Indica el motivo del descuento."
        reason == "OTRO" && note.isBlank() -> "Describe el motivo del descuento (Otro)."
        else -> null
    }
    if (error != null) return null to error
    return AdminPaymentDiscount(
        cents = cents,
        reason = reason,
        note = note.trim().ifBlank { null },
        usdCents = usdCents.takeIf { it > 0L },
    ) to null
}

class BulkPaymentFormController(
    defaultAmount: String,
    initialStatus: AdminMembershipOrderStatus,
    initialMethod: AdminPaymentMethod?,
    initialAmount: String,
    initialPayerPhone: String,
    initialLines: List<PaymentLineDraft> = emptyList(),
    initialChangeGivenCents: Long = 0L,
    initialChangeGivenUsdCents: Long = 0L,
    initialPendingUsd: String = "",
    initialPendingBs: String = "",
    initialDiscount: AdminPaymentDiscount? = null,
) {
    var isCourtesy by mutableStateOf(initialStatus == AdminMembershipOrderStatus.COURTESY)
    var amount by mutableStateOf(initialAmount.ifBlank { defaultAmount })
    // Sin saldo en esa moneda el campo muestra 0,00 (no queda vacío).
    var pendingUsdInput by mutableStateOf(initialPendingUsd.ifBlank { AMOUNT_INPUT_ZERO })
    var pendingBsInput by mutableStateOf(initialPendingBs.ifBlank { AMOUNT_INPUT_ZERO })
    var draftLines by mutableStateOf(initialLines)
    var changeGivenCents by mutableStateOf(initialChangeGivenCents)
    var changeGivenUsdCents by mutableStateOf(initialChangeGivenUsdCents)
    var walletAppliedCents by mutableStateOf(0L)
    var walletBalanceCents by mutableStateOf(0L)
    var formError by mutableStateOf<String?>(null)
    var discountBsInput by mutableStateOf(initialDiscount?.let { centsToAmountInput(it.cents) }.orEmpty())
    var discountUsdInput by mutableStateOf(initialDiscount?.usdCents?.let { centsToAmountInput(it) }.orEmpty())
    var discountReason by mutableStateOf(initialDiscount?.reason.orEmpty())
    var discountNote by mutableStateOf(initialDiscount?.note.orEmpty())

    val initialMethod: AdminPaymentMethod? = initialMethod
    val initialPayerPhone: String = initialPayerPhone
    val defaultAmount: String = defaultAmount
    val initialLines: List<PaymentLineDraft> = initialLines
    val initialChangeGivenCents: Long = initialChangeGivenCents
    val initialChangeGivenUsdCents: Long = initialChangeGivenUsdCents

    fun submit(quote: AdminExchangeRateQuote?, onSubmit: (BulkPaymentFormResult) -> Unit) {
        if (isCourtesy) {
            formError = null
            onSubmit(
                BulkPaymentFormResult(
                    status = AdminMembershipOrderStatus.COURTESY,
                    lines = emptyList(),
                    pendingUsdCents = 0L,
                    pendingBsCents = 0L,
                ),
            )
            return
        }
        // Vacío no es cero: el pendiente se escribe siempre (0,00 si no queda saldo).
        if (pendingUsdInput.isBlank() || pendingBsInput.isBlank()) {
            formError = "Escribe el pendiente en Bs y en $ (0,00 si no queda saldo)."
            return
        }
        val pendingUsdCents = parseBulkAmountToCents(pendingUsdInput)
        val pendingBsCents = parseBulkAmountToCents(pendingBsInput)
        val hasPending = pendingUsdCents > 0 || pendingBsCents > 0
        val grossTarget = parseBulkAmountToCents(amount.ifBlank { defaultAmount })
        val target = (grossTarget - walletAppliedCents).coerceAtLeast(0L)
        val lines = draftLines.ifEmpty {
            if (target > 0) {
                listOf(
                    PaymentLineDraft(
                        method = initialMethod ?: AdminPaymentMethod.CASH,
                        amountInput = amount.ifBlank { defaultAmount },
                        payerPhone = initialPayerPhone,
                    ),
                )
            } else {
                emptyList()
            }
        }.filter { line ->
            // Solo métodos con monto > 0 (permite cobro solo en $ con Bs en 0).
            parseBulkAmountToCents(line.amountInput) > 0
        }
        val hasUsd = lines.any { it.method.usesUsdAmount() && parseBulkAmountToCents(it.amountInput) > 0 }
        val (discount, discountError) = bulkDiscountOrError(
            bsInput = discountBsInput,
            reason = discountReason,
            note = discountNote,
            hasPending = hasPending,
            hasWalletCredit = walletAppliedCents > 0,
            hasLines = lines.isNotEmpty(),
            usdInput = discountUsdInput,
        )
        if (discountError != null) {
            formError = discountError
            return
        }
        if (target <= 0 && lines.isEmpty()) {
            if (walletAppliedCents > 0) {
                formError = null
                onSubmit(
                    BulkPaymentFormResult(
                        status = if (hasPending) {
                            AdminMembershipOrderStatus.PENDING
                        } else {
                            AdminMembershipOrderStatus.PAID
                        },
                        lines = emptyList(),
                        walletAppliedCents = walletAppliedCents,
                        pendingUsdCents = pendingUsdCents,
                        pendingBsCents = pendingBsCents,
                    ),
                )
                return
            }
            formError = "Indica un monto válido o al menos un método de pago."
            return
        }
        if (target <= 0 && !hasUsd && walletAppliedCents <= 0) {
            formError = "Indica un método en dólares o un monto en Bs."
            return
        }
        val amountInput = amount.ifBlank { defaultAmount }
        for (line in lines) {
            val valid = if (line.method.usesUsdAmount()) {
                parseBulkAmountToCents(line.amountInput) > 0
            } else {
                divisasLineAmountCents(line, amountInput, quote) > 0
            }
            if (!valid) {
                formError = if (line.method.usesUsdAmount() && quote == null) {
                    "Cotización BCV no disponible."
                } else if (line.method.usesUsdAmount()) {
                    "Cada línea en dólares debe tener un monto USD válido."
                } else {
                    "Cada línea debe tener un monto válido."
                }
                return
            }
            paymentReferenceValidationError(line)?.let {
                formError = it
                return
            }
        }
        formError = null
        // Vacío en $ y Bs = pago completo; con monto en cualquiera = PENDING.
        val status = if (hasPending) {
            AdminMembershipOrderStatus.PENDING
        } else {
            AdminMembershipOrderStatus.PAID
        }
        onSubmit(
            BulkPaymentFormResult(
                status = status,
                lines = lines,
                changeGivenCents = changeGivenCents,
                changeGivenUsdCents = changeGivenUsdCents,
                walletAppliedCents = walletAppliedCents,
                pendingUsdCents = pendingUsdCents,
                pendingBsCents = pendingBsCents,
                discount = discount,
            ),
        )
    }
}

@Composable
fun rememberBulkPaymentFormController(
    defaultAmount: String,
    initialStatus: AdminMembershipOrderStatus = AdminMembershipOrderStatus.PAID,
    initialMethod: AdminPaymentMethod? = AdminPaymentMethod.CASH,
    initialAmount: String = "",
    initialPayerPhone: String = "",
    initialLines: List<PaymentLineDraft> = emptyList(),
    initialChangeGivenCents: Long = 0L,
    initialChangeGivenUsdCents: Long = 0L,
    initialPendingUsd: String = "",
    initialPendingBs: String = "",
    initialDiscount: AdminPaymentDiscount? = null,
): BulkPaymentFormController = remember(
    defaultAmount,
    initialStatus,
    initialMethod,
    initialAmount,
    initialPayerPhone,
    initialLines,
    initialChangeGivenCents,
    initialChangeGivenUsdCents,
    initialPendingUsd,
    initialPendingBs,
    initialDiscount,
) {
    BulkPaymentFormController(
        defaultAmount,
        initialStatus,
        initialMethod,
        initialAmount,
        initialPayerPhone,
        initialLines,
        initialChangeGivenCents,
        initialChangeGivenUsdCents,
        initialPendingUsd,
        initialPendingBs,
        initialDiscount,
    )
}

@Composable
fun BulkRowPaymentFormFields(
    controller: BulkPaymentFormController,
    enabled: Boolean = true,
    clientId: String? = null,
    membershipOrderId: String? = null,
    extraKnownRefs: Set<String> = emptySet(),
    checkExistingMobilePayments: Boolean = false,
    /** Fecha de pago (yyyy-MM-dd) para el aviso de referencia repetida. */
    paymentDateIso: String = org.gymly.util.todayIsoDate(),
    defaultUsdAmount: String = "",
    /** USD del plan (catálogo) para etiquetas; null = mismo que [defaultUsdAmount]. */
    planCatalogUsdAmount: String? = null,
    /** Cobro de saldo en orden ya parcialmente pagada / con pendiente $. */
    balanceSettlement: Boolean = false,
    alreadyPaidSummary: String? = null,
    currentPendingSummary: String? = null,
    /** Descuento en Bs sobre el plan (solo cobro de orden de membresía). */
    allowDiscount: Boolean = false,
    modifier: Modifier = Modifier,
) {
    Column(modifier) {
        val grossCents = parseBulkAmountToCents(controller.amount.ifBlank { controller.defaultAmount })
        val dueCents = (grossCents - controller.walletAppliedCents).coerceAtLeast(0L)
        val dueInput = centsToAmountInput(dueCents)
        val dueUsdCents = parseBulkAmountToCents(defaultUsdAmount)
        val dueNowLabel = buildList {
            if (dueCents > 0L) add(formatMoney(dueCents.toInt(), GymlyCurrency.CODE))
            if (dueUsdCents > 0L) add(formatMoney(dueUsdCents.toInt(), "USD"))
        }.joinToString(" · ").ifBlank {
            formatMoney(0, GymlyCurrency.CODE)
        }

        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(10.dp))
                .clickable(enabled = enabled) { controller.isCourtesy = !controller.isCourtesy }
                .padding(vertical = 4.dp),
        ) {
            Checkbox(
                checked = controller.isCourtesy,
                onCheckedChange = { if (enabled) controller.isCourtesy = it },
                enabled = enabled,
                colors = CheckboxDefaults.colors(
                    checkedColor = AdminColors.Primary,
                    uncheckedColor = AdminColors.Outline,
                ),
            )
            Column {
                Text("Cortesía", fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                Text(
                    "Sin cobro. La membresía queda activa como cortesía.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
        }

        if (!controller.isCourtesy) {
            Spacer(Modifier.height(16.dp))
            val planUsdHint = remember(planCatalogUsdAmount, defaultUsdAmount, balanceSettlement) {
                if (balanceSettlement) return@remember null
                val catalogUsdCents = when (planCatalogUsdAmount) {
                    null -> parseBulkAmountToCents(defaultUsdAmount)
                    else -> parseBulkAmountToCents(planCatalogUsdAmount)
                }
                catalogUsdCents.takeIf { it > 0 }?.let { formatMoney(it.toInt(), "USD") }
            }
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(AdminColors.Primary.copy(alpha = 0.06f))
                    .padding(16.dp),
            ) {
                if (balanceSettlement) {
                    Text(
                        "Cobro de diferencia",
                        fontWeight = FontWeight.SemiBold,
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                    )
                    Spacer(Modifier.height(4.dp))
                    Text(
                        "Solo se registrará lo que falta. Lo ya abonado no se cobra de nuevo.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                    alreadyPaidSummary?.takeIf { it.isNotBlank() }?.let { paid ->
                        Spacer(Modifier.height(8.dp))
                        Text(
                            "Ya abonado: $paid",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                    currentPendingSummary?.takeIf { it.isNotBlank() }?.let { pending ->
                        Spacer(Modifier.height(2.dp))
                        Text(
                            "Pendiente actual: $pending",
                            color = AdminColors.Error,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium,
                        )
                    }
                    Spacer(Modifier.height(8.dp))
                    Text(
                        "Por cobrar ahora",
                        fontWeight = FontWeight.SemiBold,
                        color = AdminColors.Text,
                        fontSize = 12.sp,
                    )
                    Spacer(Modifier.height(4.dp))
                    Text(
                        dueNowLabel,
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = AdminColors.Primary,
                    )
                } else {
                    Text("Monto del plan", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
                    Spacer(Modifier.height(6.dp))
                    Text(
                        formatMoney(grossCents.toInt(), GymlyCurrency.CODE),
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                        color = AdminColors.Text,
                    )
                    planUsdHint?.let { usd ->
                        Spacer(Modifier.height(2.dp))
                        Text(
                            "Precio plan: $usd",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                }
                if (controller.walletAppliedCents > 0) {
                    Spacer(Modifier.height(4.dp))
                    Text(
                        "Saldo aplicado: ${formatMoney(controller.walletAppliedCents.toInt(), GymlyCurrency.CODE)} · " +
                            "Por cobrar: ${formatMoney(dueCents.toInt(), GymlyCurrency.CODE)}",
                        color = AdminColors.Primary,
                        fontSize = 12.sp,
                    )
                }
            }
            Spacer(Modifier.height(16.dp))
            if (clientId != null) {
                ClientAccountCreditBanner(
                    clientId = clientId,
                    pendingAmountCents = dueCents.coerceAtLeast(0L).let { if (it > 0) it else grossCents },
                    membershipOrderId = membershipOrderId,
                    enabled = enabled,
                    deferApply = membershipOrderId.isNullOrBlank(),
                    highlighted = true,
                    initialAppliedCents = controller.walletAppliedCents,
                    onCreditApplied = { applied, _ -> controller.walletAppliedCents += applied },
                    onBalanceLoaded = { controller.walletBalanceCents = it },
                )
                Spacer(Modifier.height(14.dp))
            }
            val usdOnlySettlement = balanceSettlement &&
                dueCents <= 0L &&
                parseBulkAmountToCents(defaultUsdAmount) > 0L
            val seededUsdLines = remember(usdOnlySettlement, defaultUsdAmount, controller.initialLines) {
                when {
                    controller.initialLines.isNotEmpty() -> controller.initialLines
                    usdOnlySettlement -> listOf(
                        PaymentLineDraft(
                            method = AdminPaymentMethod.DIVISAS,
                            amountInput = defaultUsdAmount,
                        ),
                    )
                    else -> emptyList()
                }
            }
            CombinedPaymentLinesForm(
                totalAmountInput = dueInput.ifBlank { controller.defaultAmount },
                defaultLineAmount = dueInput.ifBlank { controller.defaultAmount },
                defaultUsdAmount = defaultUsdAmount,
                planCatalogUsdAmount = planCatalogUsdAmount,
                referenceLabel = if (balanceSettlement) "Por cobrar" else "Plan",
                enabled = enabled,
                linesPerRow = 1,
                allowOverpay = true,
                enforcePlanCoverage = false,
                clientId = clientId,
                membershipOrderId = membershipOrderId,
                deferWalletApply = membershipOrderId.isNullOrBlank(),
                showWalletBanner = clientId == null,
                walletHighlighted = true,
                walletInitialAppliedCents = controller.walletAppliedCents,
                initialLines = seededUsdLines,
                extraKnownRefs = extraKnownRefs,
                checkExistingMobilePayments = checkExistingMobilePayments,
                paymentDateIso = paymentDateIso,
                initialChangeGivenCents = controller.initialChangeGivenCents,
                initialChangeGivenUsdCents = controller.initialChangeGivenUsdCents,
                onCreditApplied = { applied -> controller.walletAppliedCents += applied },
                onWalletBalanceLoaded = { controller.walletBalanceCents = it },
                onChangeGivenCentsChange = { controller.changeGivenCents = it },
                onChangeGivenUsdCentsChange = { controller.changeGivenUsdCents = it },
                onLinesChange = { controller.draftLines = it },
            ) { _, _, changeGiven, validate ->
                controller.changeGivenCents = changeGiven
                validate()?.let { controller.draftLines = it }
            }
            // Descuento justo debajo del monto que se cobra.
            if (allowDiscount) {
                Spacer(Modifier.height(16.dp))
                BulkDiscountSection(
                    bsInput = controller.discountBsInput,
                    onBsInputChange = { controller.discountBsInput = it },
                    usdInput = controller.discountUsdInput,
                    onUsdInputChange = { controller.discountUsdInput = it },
                    reason = controller.discountReason,
                    onReasonChange = { controller.discountReason = it },
                    note = controller.discountNote,
                    onNoteChange = { controller.discountNote = it },
                    enabled = enabled,
                )
            }
            // Pendiente en $ y/o Bs (vacío en ambos = pago completo).
            Spacer(Modifier.height(16.dp))
            Text("Pendiente", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
            Spacer(Modifier.height(4.dp))
            Text(
                if (balanceSettlement) {
                    val pendingHint = currentPendingSummary?.takeIf { it.isNotBlank() }
                    if (pendingHint != null) {
                        "Vacío = se salda el pendiente actual ($pendingHint). " +
                            "Si aún queda deuda, indica en qué moneda: \$ y/o Bs (aunque hayas cobrado en Divisas)."
                    } else {
                        "Vacío = se salda este cobro. Si aún queda deuda, elige la moneda del pendiente (\$ o Bs)."
                    }
                } else {
                    "Vacío en ambos = pago completo. El pendiente puede ser en \$ o en Bs " +
                        "(aunque el método sea Divisas/Zelle/USDT): indica cuál moneda queda por cobrar."
                },
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Spacer(Modifier.height(6.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.Top,
            ) {
                OutlinedTextField(
                    value = controller.pendingBsInput,
                    onValueChange = { controller.pendingBsInput = it },
                    enabled = enabled,
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    placeholder = {
                        Text(
                            if (balanceSettlement) "Pendiente Bs (vacío = saldar)" else "Pendiente Bs",
                        )
                    },
                    prefix = { Text("Bs ", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                OutlinedTextField(
                    value = controller.pendingUsdInput,
                    onValueChange = { controller.pendingUsdInput = it },
                    enabled = enabled,
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    placeholder = {
                        Text(
                            if (balanceSettlement) "Pendiente \$ (vacío = saldar)" else "Pendiente \$",
                        )
                    },
                    prefix = { Text("\$ ", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
            }
        } else {
            Spacer(Modifier.height(8.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(AdminColors.Primary.copy(alpha = 0.08f))
                    .padding(12.dp),
            ) {
                Icon(
                    Icons.Outlined.CardGiftcard,
                    contentDescription = null,
                    tint = AdminColors.Primary,
                    modifier = Modifier.size(20.dp),
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    "Se registrará como cortesía sin método de pago.",
                    color = AdminColors.Text,
                    fontSize = 12.sp,
                )
            }
        }
    }
}

/** Motivos de descuento de orden de membresía (mismos códigos que el backend). */
val BULK_DISCOUNT_REASONS: List<Pair<String, String>> = listOf(
    "PRONTO_PAGO" to "Pronto pago",
    "PROMOCION" to "Promoción",
    "EXCEPCION" to "Excepción acordada",
    "ERROR_PRECIO" to "Error de precio",
    "OTRO" to "Otro",
)

/** Campo de descuento en Bs con motivo y nota. Lo usan el cobro de orden y el alta de orden. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun BulkDiscountSection(
    bsInput: String,
    onBsInputChange: (String) -> Unit,
    reason: String,
    onReasonChange: (String) -> Unit,
    note: String,
    onNoteChange: (String) -> Unit,
    enabled: Boolean,
    usdInput: String = "",
    onUsdInputChange: (String) -> Unit = {},
) {
    Text("Descuento (Bs)", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
    Spacer(Modifier.height(4.dp))
    Text(
        "Vacío = sin descuento. Solo con el pendiente en 0,00. Con la tasa encendida, el descuento en $ se convierte a Bs; " +
            "con la tasa apagada, escribe también el monto en Bs.",
        color = AdminColors.TextMuted,
        fontSize = 12.sp,
    )
    Spacer(Modifier.height(6.dp))
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.Top,
    ) {
        OutlinedTextField(
            value = bsInput,
            onValueChange = onBsInputChange,
            enabled = enabled,
            modifier = Modifier.weight(1f),
            singleLine = true,
            placeholder = { Text("Descuento Bs") },
            prefix = { Text("Bs ", color = AdminColors.TextMuted) },
            shape = RoundedCornerShape(10.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = AdminColors.Primary,
                unfocusedBorderColor = AdminColors.Outline,
            ),
        )
        OutlinedTextField(
            value = usdInput,
            onValueChange = onUsdInputChange,
            enabled = enabled,
            modifier = Modifier.weight(1f),
            singleLine = true,
            placeholder = { Text("Descuento en $") },
            prefix = { Text("$ ", color = AdminColors.TextMuted) },
            shape = RoundedCornerShape(10.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = AdminColors.Primary,
                unfocusedBorderColor = AdminColors.Outline,
            ),
        )
    }
    if (parseBulkAmountToCents(bsInput) > 0L || parseBulkAmountToCents(usdInput) > 0L) {
        Spacer(Modifier.height(8.dp))
        Text("Motivo", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
        Spacer(Modifier.height(6.dp))
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            BULK_DISCOUNT_REASONS.forEach { (code, label) ->
                FilterChip(
                    selected = reason == code,
                    onClick = { onReasonChange(code) },
                    enabled = enabled,
                    label = { Text(label) },
                )
            }
        }
        // La nota solo se pide con el motivo "Otro" (obligatoria).
        if (reason == "OTRO") {
            Spacer(Modifier.height(8.dp))
            OutlinedTextField(
                value = note,
                onValueChange = onNoteChange,
                enabled = enabled,
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = {
                    Text("Motivo (obligatorio)")
                },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
        }
    }
}

@Composable
fun BulkRowPaymentSubmitButton(
    controller: BulkPaymentFormController,
    submitLabel: String = "Confirmar pago",
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
    onSubmit: (BulkPaymentFormResult) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var submitting by remember { mutableStateOf(false) }
    Button(
        onClick = {
            if (submitting || !enabled) return@Button
            submitting = true
            scope.launch {
                try {
                    val quote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
                    var accepted = false
                    controller.submit(quote) { result ->
                        accepted = true
                        onSubmit(result)
                    }
                    // Si falló validación local, re-habilitar; si se envió, dejar bloqueado
                    // hasta que el padre cierre el diálogo (evita doble cobro).
                    if (!accepted) {
                        submitting = false
                    }
                } catch (_: Throwable) {
                    submitting = false
                }
            }
        },
        enabled = enabled && !submitting,
        modifier = modifier.fillMaxWidth(),
        colors = ButtonDefaults.buttonColors(
            containerColor = AdminColors.Primary,
            contentColor = AdminColors.OnBrand,
        ),
        shape = RoundedCornerShape(10.dp),
    ) {
        Text(
            when {
                submitting -> "Registrando…"
                controller.isCourtesy -> "Confirmar cortesía"
                else -> submitLabel
            },
            fontWeight = FontWeight.SemiBold,
        )
    }
}

@Composable
fun BulkRowPaymentForm(
    defaultAmount: String,
    initialStatus: AdminMembershipOrderStatus = AdminMembershipOrderStatus.PAID,
    initialMethod: AdminPaymentMethod? = AdminPaymentMethod.CASH,
    initialAmount: String = "",
    initialPayerPhone: String = "",
    submitLabel: String = "Confirmar pago",
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
    onSubmit: (BulkPaymentFormResult) -> Unit,
) {
    val controller = rememberBulkPaymentFormController(
        defaultAmount = defaultAmount,
        initialStatus = initialStatus,
        initialMethod = initialMethod,
        initialAmount = initialAmount,
        initialPayerPhone = initialPayerPhone,
    )

    Column(modifier) {
        BulkRowPaymentFormFields(controller = controller, enabled = enabled)
        controller.formError?.let {
            Spacer(Modifier.height(8.dp))
            Text(it, color = AdminColors.Error, fontSize = 12.sp)
        }
        Spacer(Modifier.height(12.dp))
        BulkRowPaymentSubmitButton(
            controller = controller,
            submitLabel = submitLabel,
            enabled = enabled,
            onSubmit = onSubmit,
        )
    }
}

@Composable
fun BulkSharedCobroDialog(
    memberCount: Int,
    totalAmountInput: String,
    initialLines: List<PaymentLineDraft>,
    onDismiss: () -> Unit,
    onConfirm: (List<PaymentLineDraft>) -> Unit,
) {
    var draftLines by remember(initialLines, totalAmountInput) {
        mutableStateOf(
            initialLines.ifEmpty {
                listOf(PaymentLineDraft(amountInput = totalAmountInput))
            },
        )
    }
    var formError by remember { mutableStateOf<String?>(null) }
    var exchangeQuote by remember { mutableStateOf<org.gymly.model.AdminExchangeRateQuote?>(null) }

    LaunchedEffect(Unit) {
        exchangeQuote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
    }

    Dialog(onDismissRequest = onDismiss) {
        Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
            VerticalScrollColumn(
                Modifier
                    .widthIn(min = 580.dp, max = 760.dp)
                    .fillMaxWidth(0.96f)
                    .clip(RoundedCornerShape(20.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(24.dp)
                    .heightIn(max = 720.dp),
        ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Column(Modifier.weight(1f)) {
                        Text("Cobrar", fontWeight = FontWeight.Bold, fontSize = 20.sp)
                        Text(
                            "$memberCount ${if (memberCount == 1) "socio" else "socios"} · combina métodos de pago",
                            color = AdminColors.TextMuted,
                            fontSize = 14.sp,
                        )
                    }
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                    }
                }
                Spacer(Modifier.height(16.dp))
                Column(
                    Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(12.dp))
                        .background(AdminColors.Primary.copy(alpha = 0.06f))
                        .padding(16.dp),
                ) {
                    Text("Total a cobrar", color = AdminColors.TextMuted, fontSize = 13.sp)
                    Text(
                        formatMoney(parseBulkAmountToCents(totalAmountInput).toInt(), GymlyCurrency.CODE),
                        fontWeight = FontWeight.Bold,
                        fontSize = 28.sp,
                        color = AdminColors.Primary,
                    )
                }
                Spacer(Modifier.height(16.dp))
                CombinedPaymentLinesForm(
                    totalAmountInput = totalAmountInput,
                    defaultLineAmount = totalAmountInput,
                    allowOverpay = true,
                    enforcePlanCoverage = false,
                    multiClientOverpay = memberCount > 1,
                    onLinesChange = { draftLines = it },
                ) { _, _, _, _ -> }
                formError?.let {
                    Spacer(Modifier.height(8.dp))
                    Text(it, color = AdminColors.Error, fontSize = 12.sp)
                }
                Spacer(Modifier.height(16.dp))
                Button(
                    onClick = {
                        val target = parseBulkAmountToCents(totalAmountInput)
                        if (target <= 0) {
                            formError = "Monto total inválido."
                            return@Button
                        }
                        val lines = draftLines.ifEmpty {
                            listOf(PaymentLineDraft(amountInput = totalAmountInput))
                        }
                        for (line in lines) {
                            if (!paymentLineHasPositiveAmount(line, totalAmountInput, exchangeQuote)) {
                                formError = if (line.method.usesUsdAmount()) {
                                    "Cada línea en dólares debe tener un monto USD válido."
                                } else {
                                    "Cada línea debe tener un monto válido."
                                }
                                return@Button
                            }
                            paymentReferenceValidationError(line)?.let {
                                formError = it
                                return@Button
                            }
                        }
                        formError = null
                        onConfirm(lines)
                    },
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text("Confirmar cobro", fontWeight = FontWeight.SemiBold)
                }
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("Cancelar") }
                }
            }
        }
    }
}

@Composable
fun BulkRowPaymentDialog(
    personLabel: String,
    defaultAmount: String,
    defaultUsdAmount: String = "",
    planCatalogUsdAmount: String? = null,
    initialStatus: AdminMembershipOrderStatus,
    initialMethod: AdminPaymentMethod?,
    initialAmount: String,
    initialPayerPhone: String,
    clientId: String? = null,
    membershipOrderId: String? = null,
    initialLines: List<PaymentLineDraft> = emptyList(),
    initialChangeGivenCents: Long = 0L,
    initialChangeGivenUsdCents: Long = 0L,
    initialPendingUsd: String = "",
    initialPendingBs: String = "",
    initialDiscount: AdminPaymentDiscount? = null,
    extraKnownRefs: Set<String> = emptySet(),
    checkExistingMobilePayments: Boolean = false,
    /** Fecha de pago (yyyy-MM-dd) para el aviso de referencia repetida; por defecto hoy. */
    paymentDateIso: String = org.gymly.util.todayIsoDate(),
    /** Cobro de saldo (orden con abonos / pendiente $). */
    balanceSettlement: Boolean = false,
    alreadyPaidSummary: String? = null,
    currentPendingSummary: String? = null,
    /** Descuento en Bs; solo lo activa el cobro de orden de membresía. */
    allowDiscount: Boolean = false,
    onDismiss: () -> Unit,
    onConfirm: (
        status: AdminMembershipOrderStatus,
        method: AdminPaymentMethod?,
        amountCents: Long,
        payerPhone: String?,
        proof: PickedImagePayload?,
    ) -> Unit = { _, _, _, _, _ -> },
    onConfirmResult: ((BulkPaymentFormResult) -> Unit)? = null,
) {
    val controller = rememberBulkPaymentFormController(
        defaultAmount = defaultAmount,
        initialStatus = initialStatus,
        initialMethod = initialMethod,
        initialAmount = initialAmount,
        initialPayerPhone = initialPayerPhone,
        initialLines = initialLines,
        initialChangeGivenCents = initialChangeGivenCents,
        initialChangeGivenUsdCents = initialChangeGivenUsdCents,
        initialPendingUsd = initialPendingUsd,
        initialPendingBs = initialPendingBs,
        initialDiscount = initialDiscount,
    )

    Dialog(onDismissRequest = onDismiss) {
        Box(
            Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center,
        ) {
            val scrollState = rememberScrollState()
            Column(
                Modifier
                    .widthIn(min = 580.dp, max = 760.dp)
                    .fillMaxWidth(0.96f)
                    .clip(RoundedCornerShape(20.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(24.dp)
                    .heightIn(max = 720.dp),
            ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Column(Modifier.weight(1f)) {
                        Text(
                            if (balanceSettlement) "Cobro de saldo pendiente" else "Pago de membresía",
                            fontWeight = FontWeight.Bold,
                            fontSize = 20.sp,
                        )
                        Text(personLabel, color = AdminColors.TextMuted, fontSize = 14.sp)
                    }
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                    }
                }
                Spacer(Modifier.height(12.dp))
                Text(
                    if (balanceSettlement) {
                        "Este cobro solo registra la diferencia pendiente. Usa Enter / flechas entre montos."
                    } else {
                        "Usa Enter / flechas para pasar entre montos. Desplázate si hay más campos (vuelto, métodos)."
                    },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                Spacer(Modifier.height(10.dp))
                Column(
                    Modifier
                        .weight(1f, fill = false)
                        .heightIn(max = 480.dp)
                        .fillMaxWidth(),
                ) {
                    VerticalScrollColumn(
                        Modifier.fillMaxSize(),
                        state = scrollState,
                    ) {
                        BulkRowPaymentFormFields(
                            controller = controller,
                            clientId = clientId,
                            membershipOrderId = membershipOrderId,
                            extraKnownRefs = extraKnownRefs,
                            checkExistingMobilePayments = checkExistingMobilePayments,
                            paymentDateIso = paymentDateIso,
                            defaultUsdAmount = defaultUsdAmount,
                            planCatalogUsdAmount = planCatalogUsdAmount,
                            balanceSettlement = balanceSettlement,
                            alreadyPaidSummary = alreadyPaidSummary,
                            currentPendingSummary = currentPendingSummary,
                            allowDiscount = allowDiscount,
                        )
                        if (scrollState.canScrollForward || scrollState.canScrollBackward) {
                            Spacer(Modifier.height(8.dp))
                            Text(
                                if (scrollState.canScrollForward) {
                                    "↓ Hay más campos abajo (vuelto en Bs, agregar método…)"
                                } else {
                                    "↑ Puedes subir para revisar métodos de pago"
                                },
                                color = AdminColors.Primary,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                    }
                }
                controller.formError?.let {
                    Spacer(Modifier.height(8.dp))
                    Text(it, color = AdminColors.Error, fontSize = 12.sp)
                }
                Spacer(Modifier.height(12.dp))
                val bsDue = (
                    parseBulkAmountToCents(controller.amount.ifBlank { controller.defaultAmount }) -
                        controller.walletAppliedCents
                    ).coerceAtLeast(0L)
                val usdOnLines = controller.draftLines.sumOf { line ->
                    if (line.method.usesUsdAmount()) parseBulkAmountToCents(line.amountInput) else 0L
                }
                val usdDueHint = parseBulkAmountToCents(defaultUsdAmount)
                BulkRowPaymentSubmitButton(
                    controller = controller,
                    submitLabel = "Confirmar pago",
                    enabled = controller.isCourtesy ||
                        controller.walletAppliedCents > 0 ||
                        bsDue > 0L ||
                        usdOnLines > 0L ||
                        // Cobro de saldo solo en $: habilitar aunque Bs sea 0.
                        (balanceSettlement && usdDueHint > 0L),
                    onSubmit = { result ->
                        if (onConfirmResult != null) {
                            onConfirmResult(result)
                        } else {
                            onConfirm(
                                result.status,
                                result.method,
                                result.amountCents,
                                result.payerPhone,
                                result.proof,
                            )
                        }
                    },
                )
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("Cancelar") }
                }
            }
        }
    }
}

@Composable
fun BulkRowPaymentCell(
    status: AdminMembershipOrderStatus,
    method: AdminPaymentMethod?,
    paymentConfigured: Boolean,
    enabled: Boolean,
    onOpenPayment: () -> Unit,
    onClearPayment: () -> Unit,
    paymentSummary: String? = null,
) {
    Column(horizontalAlignment = Alignment.End) {
        val statusLabel = when {
            !paymentConfigured -> "Pendiente"
            !paymentSummary.isNullOrBlank() -> paymentSummary
            status == AdminMembershipOrderStatus.COURTESY -> "Cortesía"
            status == AdminMembershipOrderStatus.PAID -> method?.labelEs()?.let { "Pagado · $it" } ?: "Pagado"
            status == AdminMembershipOrderStatus.PENDING && method != null ->
                "Pendiente · ${method.labelEs()}"
            else -> status.labelEs()
        }
        val statusColor = when {
            !paymentConfigured -> AdminColors.TextMuted
            status == AdminMembershipOrderStatus.COURTESY -> AdminColors.Primary
            status == AdminMembershipOrderStatus.PAID -> AdminColors.Primary
            else -> AdminColors.TextMuted
        }
        Text(
            statusLabel,
            color = statusColor,
            fontSize = 12.sp,
            fontWeight = FontWeight.Medium,
            maxLines = 3,
        )
        Spacer(Modifier.height(8.dp))
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Button(
                onClick = onOpenPayment,
                enabled = enabled,
                modifier = Modifier
                    .heightIn(min = 52.dp)
                    .widthIn(min = 148.dp),
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                    disabledContainerColor = AdminColors.Outline.copy(alpha = 0.45f),
                    disabledContentColor = AdminColors.TextMuted,
                ),
                elevation = ButtonDefaults.buttonElevation(
                    defaultElevation = 3.dp,
                    pressedElevation = 1.dp,
                ),
            ) {
                Icon(
                    Icons.Outlined.Payments,
                    contentDescription = null,
                    modifier = Modifier.size(22.dp),
                    tint = AdminColors.OnBrand,
                )
                Spacer(Modifier.width(8.dp))
                Text("Cobrar", fontWeight = FontWeight.Bold, fontSize = 16.sp)
            }
            if (paymentConfigured) {
                Text(
                    "×",
                    color = AdminColors.Error,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier
                        .clickable(enabled = enabled, onClick = onClearPayment)
                        .padding(8.dp),
                )
            }
        }
    }
}

fun parseBulkAmountToCents(raw: String): Long {
    val cleaned = raw.trim().replace(",", ".").filter { it.isDigit() || it == '.' }
    if (cleaned.isBlank()) return 0L
    val value = cleaned.toDoubleOrNull() ?: return 0L
    return (value * 100.0).toLong()
}
